import { useEffect, useState } from 'react';
import type { DictItemDto } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 批量设置特殊物品。
 *
 * ★ 用户原话:「批量操作里面增加一个设置冻品的功能,就是设置特殊物品的功能,
 *   可以批量设置特殊物品,设置后这些选择的订单就都有这个特殊物品了」。
 *
 *   典型场景:一整批团购货全是冻品。一张张点开去勾是几十次点击,
 *   而不勾的后果是司机不知道要带保温箱 —— 货到就化了。
 *
 * ★ 默认是**加上**,不是替换。
 *
 *   「都有这个特殊物品了」说的是「确保有」。替换的话,某一单原本标着
 *   「易碎」的会被这一次批量悄悄抹掉,而那条标记是司机决定怎么搬的依据。
 *   要「改成只有这些」得显式勾那个选项,而勾上之后按钮的字也跟着变 ——
 *   一个会抹掉数据的操作,不该和「加一个标记」长得一样。
 */
export function SpecialItemsDialog({
  orders,
  onClose,
  onDone,
}: {
  /** 选中的那几单 —— 要它们**当前**的特殊物品来预勾,不能只给 id */
  orders: Array<{ id: string; specialItems?: Array<{ code: string }> | null }>;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [dict, setDict] = useState<DictItemDto[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ updated: number; skipped: number } | null>(null);

  useEffect(() => {
    void api<DictItemDto[]>('/dict/SPECIAL_ITEM')
      .then(setDict)
      .catch(() => setErr('读不到特殊物品的清单 —— 可能本店没开这个功能'));
  }, []);

  /*
   * ★ 「这几单里已经有几单标了」要显示出来。
   *
   *   不显示的话,他勾上「冻品」点确认,结果只改了 3 单 ——
   *   而另外 17 单本来就有。那个数字对不上会被当成没生效。
   */
  const countWith = (code: string): number =>
    orders.filter((o) => (o.specialItems ?? []).some((s) => s.code === code)).length;

  async function save() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      setDone(
        await api<{ updated: number; skipped: number }>('/orders/batch-special-items', {
          method: 'POST',
          body: { orderIds: orders.map((o) => o.id), specialItems: picked, replace },
        }),
      );
      await onDone();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '设置失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="批量设置特殊物品"
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            设置特殊物品 · {orders.length} 单
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-xl text-slate-400">
            <InlineIcon icon={X} />
          </button>
        </div>

        {err && (
          <div
            className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700
                          dark:bg-red-950/40 dark:text-red-300"
          >
            {err}
          </div>
        )}

        {done ? (
          <div className="space-y-3">
            <p className="text-slate-900 dark:text-slate-100">
              改了 {done.updated} 单。
              {done.skipped > 0 && (
                /*
                  ★ 没改的要说出来,而且说清为什么。
                    「已经是这样」和「已经送完改不了」都算没改,
                    只报一个数字的话他会以为失败了。
                */
                <span className="block text-sm text-slate-500">
                  另外 {done.skipped} 单没动 —— 要么本来就是这样,要么已经送完/取消了。
                </span>
              )}
            </p>
            <button
              onClick={onClose}
              className="h-10 w-full rounded-lg bg-brand-600 font-medium text-white"
            >
              完成
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              {dict.length === 0 ? (
                <p className="text-sm text-slate-500">没有可选的特殊物品。</p>
              ) : (
                dict.map((d) => {
                  const has = countWith(d.code);
                  return (
                    <label key={d.code} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        aria-label={d.label}
                        checked={picked.includes(d.code)}
                        onChange={(e) =>
                          setPicked((prev) =>
                            e.target.checked ? [...prev, d.code] : prev.filter((c) => c !== d.code),
                          )
                        }
                      />
                      <span className="text-slate-900 dark:text-slate-100">{d.label}</span>
                      {has > 0 && <span className="text-xs text-slate-400">已有 {has} 单标了</span>}
                    </label>
                  );
                })
              )}
            </div>

            <label
              className="flex items-start gap-2 border-t border-slate-100 pt-3 text-sm
                              dark:border-slate-700"
            >
              <input
                type="checkbox"
                aria-label="改成只有这些"
                className="mt-1"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
              />
              <span>
                <span className="text-slate-900 dark:text-slate-100">改成只有这些</span>
                {/*
                  ★ 这个选项的后果要写在眼前:它会抹掉别的标记。
                    不写的话,想「加个冻品」的人勾上它,把二十单的「易碎」
                    一次抹光 —— 而那条标记是司机决定怎么搬的依据,
                    抹掉之后没有任何地方会提醒。
                */}
                <span className="block text-xs text-amber-700 dark:text-amber-400">
                  会把这些单原有的其他标记抹掉(比如「易碎」)。不勾的话是在原有的基础上加。
                </span>
              </span>
            </label>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="h-10 flex-1 rounded-lg border border-slate-300 text-slate-700
                           dark:border-slate-600 dark:text-slate-300"
              >
                取消
              </button>
              <button
                onClick={() => void save()}
                disabled={busy || (picked.length === 0 && !replace)}
                className="h-10 flex-1 rounded-lg bg-brand-600 font-medium text-white
                           disabled:opacity-40"
              >
                {busy
                  ? '设置中…'
                  : picked.length === 0
                    ? replace
                      ? `清空 ${orders.length} 单的标记`
                      : '先选一样'
                    : replace
                      ? `改成只有这些(${orders.length} 单)`
                      : `给 ${orders.length} 单加上`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
